import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { ContactUploadPathKey, IFileToS3DAO } from '../../../../../api/file-to-s3.dao';
import { IAudioWithUrl, IContactUploadRequestParams, S3ObjectMetadata } from '../../../../../api/shared.dto';
import {
    AUDIO_VALIDATION_DURATION_ERROR_MESSAGE,
    AUDIO_VALIDATION_FORMAT_ERROR_MESSAGE,
    AUDIO_VALIDATION_SIZE_ERROR_MESSAGE,
} from '../../../../../constants/file.constants';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { getAudioMimeType, isAudioDurationValid, isFileSizeValid } from '../../../../../infrastructure/utils/common.utils';
import { alertServerError } from '../../../../global/modal/alerts-host/alert-templates';

interface IAudioFileSnapshot {
    file: File;
    objectKey: string;
    storedAudioUrl: string;
    storedTitle: string;
    audioChanged: boolean;
    previewUrl: string;
    audioError: string;
}

export default class UploadAudioS3VM {
    @mobx.observable
    public file: File = null;
    @mobx.observable
    public objectKey = '';
    @mobx.observable
    public isAudioUploading = false;
    @mobx.observable
    public isAudioChecking = false;
    @mobx.observable
    public audioError = '';
    @mobx.observable
    private storedAudioUrl = '';
    @mobx.observable
    private storedTitle = '';
    @mobx.observable
    private audioChanged = false;
    private previewUrl = '';
    private audioSnapshot: IAudioFileSnapshot;
    private audioUploadId = 0;
    private readonly uploadParams: IContactUploadRequestParams;

    constructor(
        private readonly contactId: number,
        storedAudio: IAudioWithUrl = null,
        private readonly pathKey: ContactUploadPathKey = 'rolodex-contact',
        @IoC.Inject private readonly fileToS3DAO: IFileToS3DAO = IoC.Container.get(IFileToS3DAO),
    ) {
        this.uploadParams = { contactId: contactId };
        this.storedAudioUrl = storedAudio?.audioUrl ?? '';
        this.storedTitle = storedAudio?.title ?? '';
        this.rememberAudioFile();
    }

    public hasAudio = (): boolean => {
        return !!this.file || !!this.storedAudioUrl;
    };

    public hasNewlyUploadedAudio = (): boolean => {
        return this.objectKey !== this.audioSnapshot.objectKey;
    };

    public getAudioName = (): string => {
        if (this.file) {
            return this.file.name;
        }
        return this.storedTitle;
    };

    public getAudioSource = (): string => {
        return this.previewUrl || this.storedAudioUrl;
    };

    @mobx.action
    public setAudio = async (file: File): Promise<boolean> => {
        mobx.runInAction(() => {
            this.audioError = '';
        });

        if (!file) {
            return false;
        }

        const uploadId = ++this.audioUploadId;
        this.isAudioChecking = true;
        const mimeType = await getAudioMimeType(file);
        if (uploadId !== this.audioUploadId) {
            return false;
        }
        const error = await this.validate(file, mimeType);
        if (uploadId !== this.audioUploadId) {
            return false;
        }
        if (error) {
            mobx.runInAction(() => {
                this.audioError = error;
                this.isAudioChecking = false;
                this.isAudioUploading = false;
            });
            return false;
        }

        const uploadFile = file.type === mimeType ? file : new File([file], file.name, { type: mimeType });

        const previousFile = this.file;
        const previousPreviewUrl = this.previewUrl;
        mobx.runInAction(() => {
            this.isAudioChecking = false;
            this.isAudioUploading = true;
            this.file = uploadFile;
            this.previewUrl = (window.URL || window.webkitURL).createObjectURL(uploadFile);
        });

        const uploaded = await this.uploadAudio(uploadId);
        if (uploadId !== this.audioUploadId) {
            return false;
        }
        if (uploaded) {
            if (previousPreviewUrl !== this.audioSnapshot.previewUrl) {
                UploadAudioS3VM.revokeObjectUrl(previousPreviewUrl);
            }
            return true;
        }
        this.revokePreview();
        mobx.runInAction(() => {
            this.file = previousFile;
            this.previewUrl = previousPreviewUrl;
        });
        return false;
    };

    @mobx.action
    public deleteAudio = (): void => {
        this.replaceAudio(true);
    };

    @mobx.action
    public completeAudioRemoval = (): void => {
        this.replaceAudio(false);
    };

    public getAudioToApi = (): S3ObjectMetadata | null => {
        if (!this.audioChanged) {
            return null;
        }
        return { title: this.getAudioName(), objectKey: this.objectKey };
    };

    @mobx.action
    public updateStoredAudio = (storedAudio?: IAudioWithUrl): void => {
        this.storedAudioUrl = storedAudio?.audioUrl ?? '';
        this.storedTitle = storedAudio?.title ?? '';
        this.rememberAudioFile();
    };

    @mobx.action
    public recoverAudioFile = (): void => {
        this.audioUploadId += 1;
        this.isAudioUploading = false;
        this.isAudioChecking = false;
        if (this.previewUrl !== this.audioSnapshot.previewUrl) {
            UploadAudioS3VM.revokeObjectUrl(this.previewUrl);
        }
        this.file = this.audioSnapshot.file;
        this.objectKey = this.audioSnapshot.objectKey;
        this.storedAudioUrl = this.audioSnapshot.storedAudioUrl;
        this.storedTitle = this.audioSnapshot.storedTitle;
        this.audioChanged = this.audioSnapshot.audioChanged;
        this.previewUrl = this.audioSnapshot.previewUrl;
        this.audioError = this.audioSnapshot.audioError;
    };

    private rememberAudioFile = (): void => {
        if (this.audioSnapshot?.previewUrl && this.audioSnapshot.previewUrl !== this.previewUrl) {
            UploadAudioS3VM.revokeObjectUrl(this.audioSnapshot.previewUrl);
        }
        this.audioSnapshot = {
            file: this.file,
            objectKey: this.objectKey,
            storedAudioUrl: this.storedAudioUrl,
            storedTitle: this.storedTitle,
            audioChanged: this.audioChanged,
            previewUrl: this.previewUrl,
            audioError: this.audioError,
        };
    };

    private validate = async (file: File, mimeType: string): Promise<string> => {
        if (!mimeType) {
            return AUDIO_VALIDATION_FORMAT_ERROR_MESSAGE;
        }
        if (!isFileSizeValid(file)) {
            return AUDIO_VALIDATION_SIZE_ERROR_MESSAGE;
        }
        if (!await isAudioDurationValid(file)) {
            return AUDIO_VALIDATION_DURATION_ERROR_MESSAGE;
        }
        return '';
    };

    private replaceAudio = (audioChanged: boolean): void => {
        this.audioUploadId += 1;
        this.isAudioUploading = false;
        this.isAudioChecking = false;
        if (this.previewUrl !== this.audioSnapshot.previewUrl) {
            this.revokePreview();
        } else {
            this.previewUrl = '';
        }
        this.file = null;
        this.objectKey = '';
        this.storedAudioUrl = '';
        this.storedTitle = '';
        this.audioError = '';
        this.audioChanged = audioChanged;
        this.rememberAudioFile();
    };

    private uploadAudio = async (uploadId: number): Promise<boolean> => {
        try {
            mobx.runInAction(() => {
                this.isAudioUploading = true;
            });
            const response = await this.fileToS3DAO.uploadFileToS3(this.file, this.uploadParams, this.pathKey);
            if (uploadId !== this.audioUploadId) {
                return false;
            }
            mobx.runInAction(() => {
                this.objectKey = response.objectKey;
                this.audioChanged = true;
            });
            return true;
        } catch (e) {
            if (uploadId !== this.audioUploadId) {
                return false;
            }
            alertServerError('Error', IoC.Container.get(IErrorParsing).extractMessage(e));
            return false;
        } finally {
            if (uploadId === this.audioUploadId) {
                mobx.runInAction(() => {
                    this.isAudioUploading = false;
                });
            }
        }
    };

    private revokePreview = (): void => {
        UploadAudioS3VM.revokeObjectUrl(this.previewUrl);
        this.previewUrl = '';
    };

    private static revokeObjectUrl = (url: string): void => {
        if (url) {
            (window.URL || window.webkitURL).revokeObjectURL(url);
        }
    };
}
